/**
 * SFX — synthesized Web Audio sound effects. Zero assets, CSP-safe,
 * offline-friendly, and quiet by design.
 *
 * Browser policy notes:
 * • AudioContext is created/resumed lazily on first use; browsers only
 *   allow it after a user gesture, so we also attach a one-time global
 *   `pointerdown` unlock listener from the game shell.
 * • Everything is wrapped in try/catch — audio must never break the game.
 */

const STORAGE_KEY = 'met:sound';

type SfxName =
  | 'click'
  | 'sale'
  | 'po'
  | 'coin'
  | 'event'
  | 'bad'
  | 'good'
  | 'research'
  | 'gameover'
  | 'fanfare'
  | 'sparkle'
  | 'trombone'
  | 'cashBig'
  | 'bell';

class SoundFx {
  private ctx: AudioContext | null = null;
  enabled: boolean;

  constructor() {
    try {
      this.enabled = localStorage.getItem(STORAGE_KEY) !== 'off';
    } catch {
      this.enabled = true;
    }
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    try {
      localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
    } catch {
      /* private mode — session-only preference */
    }
    if (on) this.play('click');
  }

  /** Call once on first user gesture to satisfy autoplay policies. */
  unlock(): void {
    if (!this.enabled) return;
    this.ensureCtx();
  }

  private ensureCtx(): AudioContext | null {
    try {
      if (!this.ctx) {
        const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AC) return null;
        this.ctx = new AC();
      }
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return this.ctx;
    } catch {
      return null;
    }
  }

  private tone(
    ctx: AudioContext,
    opts: { freq: number; endFreq?: number; dur: number; delay?: number; type?: OscillatorType; gain?: number },
  ): void {
    const t0 = ctx.currentTime + (opts.delay ?? 0);
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = opts.type ?? 'sine';
    osc.frequency.setValueAtTime(opts.freq, t0);
    if (opts.endFreq !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, opts.endFreq), t0 + opts.dur);
    }
    const peak = opts.gain ?? 0.08;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + Math.min(0.02, opts.dur / 3));
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.dur);
    osc.connect(g).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + opts.dur + 0.02);
  }

  play(name: SfxName): void {
    if (!this.enabled) return;
    const ctx = this.ensureCtx();
    if (!ctx || ctx.state === 'suspended') return; // no gesture yet — stay silent
    try {
      switch (name) {
        case 'click':
          this.tone(ctx, { freq: 1800, dur: 0.04, type: 'square', gain: 0.03 });
          break;
        case 'sale':
          // Little two-note "ka-ching" flourish.
          this.tone(ctx, { freq: 880, dur: 0.07, gain: 0.06 });
          this.tone(ctx, { freq: 1318, dur: 0.11, delay: 0.07, gain: 0.07 });
          break;
        case 'po':
          // Heavy freight thud.
          this.tone(ctx, { freq: 170, endFreq: 90, dur: 0.16, type: 'triangle', gain: 0.11 });
          this.tone(ctx, { freq: 2400, dur: 0.03, type: 'square', gain: 0.025, delay: 0.01 });
          break;
        case 'coin':
          this.tone(ctx, { freq: 987, dur: 0.06, gain: 0.06 });
          this.tone(ctx, { freq: 1567, dur: 0.14, delay: 0.06, gain: 0.06 });
          break;
        case 'event':
          // Neutral attention chime.
          this.tone(ctx, { freq: 494, dur: 0.09, type: 'sine', gain: 0.06 });
          this.tone(ctx, { freq: 659, dur: 0.12, delay: 0.09, gain: 0.055 });
          break;
        case 'bad':
          // Ominous minor-second buzz.
          this.tone(ctx, { freq: 220, dur: 0.14, type: 'sawtooth', gain: 0.05 });
          this.tone(ctx, { freq: 233, dur: 0.18, delay: 0.13, type: 'sawtooth', gain: 0.05 });
          break;
        case 'good':
          // Bright major arpeggio.
          this.tone(ctx, { freq: 523, dur: 0.08, gain: 0.05 });
          this.tone(ctx, { freq: 659, dur: 0.08, delay: 0.08, gain: 0.05 });
          this.tone(ctx, { freq: 784, dur: 0.16, delay: 0.16, gain: 0.06 });
          break;
        case 'research':
          this.tone(ctx, { freq: 330, endFreq: 660, dur: 0.22, type: 'sine', gain: 0.06 });
          break;
        case 'gameover':
          this.tone(ctx, { freq: 392, dur: 0.22, type: 'triangle', gain: 0.07 });
          this.tone(ctx, { freq: 330, dur: 0.22, delay: 0.22, type: 'triangle', gain: 0.07 });
          this.tone(ctx, { freq: 262, dur: 0.45, delay: 0.44, type: 'triangle', gain: 0.08 });
          break;
        case 'fanfare':
          // Achievement unlock: bright major fanfare + sparkle on top.
          this.tone(ctx, { freq: 523, dur: 0.11, type: 'square', gain: 0.05 });
          this.tone(ctx, { freq: 659, dur: 0.11, delay: 0.11, type: 'square', gain: 0.05 });
          this.tone(ctx, { freq: 784, dur: 0.11, delay: 0.22, type: 'square', gain: 0.055 });
          this.tone(ctx, { freq: 1046, dur: 0.3, delay: 0.33, type: 'triangle', gain: 0.07 });
          break;
        case 'sparkle':
          // Viral moment: rapid ascending pentatonic glitter.
          [523, 587, 659, 784, 880, 1046].forEach((f, i) =>
            this.tone(ctx, { freq: f, dur: 0.09, delay: i * 0.06, type: 'sine', gain: 0.05 }),
          );
          break;
        case 'trombone':
          // The classic comedy wah-wah-wah-waaah for going bust.
          this.tone(ctx, { freq: 233, endFreq: 220, dur: 0.28, type: 'sawtooth', gain: 0.055 });
          this.tone(ctx, { freq: 220, endFreq: 207, dur: 0.28, delay: 0.3, type: 'sawtooth', gain: 0.055 });
          this.tone(ctx, { freq: 208, endFreq: 196, dur: 0.28, delay: 0.6, type: 'sawtooth', gain: 0.055 });
          this.tone(ctx, { freq: 196, endFreq: 138, dur: 0.85, delay: 0.92, type: 'sawtooth', gain: 0.06 });
          break;
        case 'cashBig':
          // Acquisition exit: double register cha-ching into a fanfare tail.
          this.tone(ctx, { freq: 987, dur: 0.07, gain: 0.07 });
          this.tone(ctx, { freq: 1567, dur: 0.12, delay: 0.07, gain: 0.07 });
          this.tone(ctx, { freq: 1318, dur: 0.07, delay: 0.2, gain: 0.06 });
          this.tone(ctx, { freq: 2093, dur: 0.16, delay: 0.27, gain: 0.06 });
          this.tone(ctx, { freq: 523, dur: 0.12, delay: 0.5, type: 'square', gain: 0.05 });
          this.tone(ctx, { freq: 784, dur: 0.34, delay: 0.62, type: 'triangle', gain: 0.07 });
          break;
        case 'bell':
          // Soft quarter-close gong.
          this.tone(ctx, { freq: 660, dur: 0.9, type: 'sine', gain: 0.05 });
          this.tone(ctx, { freq: 1320, dur: 0.45, type: 'sine', gain: 0.02 });
          break;
      }
    } catch {
      /* never let audio break gameplay */
    }
  }
}

export const sfx = new SoundFx();
